import { c as _c } from "react/compiler-runtime";
import { createContext, type Dispatch, type PropsWithChildren, useContext, useEffect, useState, useSyncExternalStore } from 'react';
import { createReducerStore, type ReducerStore } from '../helpers/createReducerStore';
import storage from '../helpers/storage';
import { getDefaultLocalPreference, type LocalPreferenceAction, localPreferenceActions, type LocalPreferenceState } from './context';
import { localPreferenceReducer } from './reducers/localPreferenceReducer';

// The device's display preferences (feed filters, theme, video mute, the
// typed data filter) in a store, not in context state. The context value
// (the store) never changes; each component subscribes to the fields it
// renders through useLocalPreferenceSelector. A keystroke in the data filter
// used to rerender everything that read the theme — on mobile, every
// component that styles itself.

export type LocalPreferenceStore = ReducerStore<LocalPreferenceState, LocalPreferenceAction>;
const STORAGE_KEY = 'localPreference';
// Typing in the data filter changes the state on every keystroke; storage is
// written once the typing pauses.
const PERSIST_DELAY_MS = 300;

// Both the server render and the first client render start here, and the
// stored preferences load right after mount.
const startingState = getDefaultLocalPreference();

// Outside a provider (some tests) nothing changes, as the old context's
// no-op dispatch did.
const LocalPreferenceContext = createContext<LocalPreferenceStore>(createReducerStore((state: LocalPreferenceState, _action: LocalPreferenceAction) => state, startingState));

/** The store itself. It never changes, so this never rerenders. */
export const useLocalPreferenceStore = () => {
  return useContext(LocalPreferenceContext);
};

/**
 * One slice of the preferences. The caller rerenders only when the selected
 * value changes (Object.is), so a selector must return a field or a primitive,
 * never a new object or array.
 */
export const useLocalPreferenceSelector = selector => {
  const $ = _c(5);
  const store = useContext(LocalPreferenceContext);
  let t0;
  if ($[0] !== selector || $[1] !== store) {
    t0 = () => selector(store.getState());
    $[0] = selector;
    $[1] = store;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== selector) {
    t1 = () => selector(startingState);
    $[3] = selector;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return useSyncExternalStore(store.subscribe, t0, t1);
};

/**
 * The whole state and a dispatch, as the context used to provide them.
 * Rerenders on every change, keystrokes included; prefer
 * useLocalPreferenceSelector.
 */
export const useLocalPreferenceState = () => {
  const $ = _c(3);
  const store = useContext(LocalPreferenceContext);
  const state = useLocalPreferenceSelector(_temp);
  let t0;
  if ($[0] !== state || $[1] !== store.dispatch) {
    t0 = {
      state,
      dispatch: store.dispatch
    };
    $[0] = state;
    $[1] = store.dispatch;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
};
export const LocalPreferenceContextProvider = ({
  children
}: PropsWithChildren) => {
  // One store per provider, never per module: the Next server renders every
  // request in one process.
  const [store] = useState(() => createReducerStore(localPreferenceReducer, getDefaultLocalPreference()));

  // Load preferences from storage on mount, then keep storage in step with
  // the store: one writer, which always writes the whole current state. Each
  // update used to write its own copy built from the state it rendered with,
  // so two updates in one tick lost the first.
  // Uses storage abstraction: localStorage (web) or AsyncStorage (mobile)
  useEffect(() => {
    let ignore = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const persist = () => {
      timer = null;
      void storage.setItem(STORAGE_KEY, JSON.stringify(store.getState()));
    };
    const flush = () => {
      if (!timer) return;
      clearTimeout(timer);
      persist();
    };
    const loadFromStorage = async () => {
      try {
        const data = await storage.getItem(STORAGE_KEY);
        if (ignore || !data) return;
        const parsed = JSON.parse(data) as LocalPreferenceState;
        // Validate that required fields are present
        if (parsed.postFilterType !== null && parsed.postFilterType !== undefined) {
          store.dispatch({
            type: localPreferenceActions.initializePreference,
            payload: parsed
          });
        }
      } catch (error) {
        console.error('Error loading localPreference from storage:', error);
      }
    };
    void loadFromStorage();
    const unsubscribe = store.subscribe(() => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(persist, PERSIST_DELAY_MS);
    });
    // A tab closed inside the delay still saves what was typed. React Native
    // has no page events; there the app's storage write simply runs late.
    const canListen = typeof window !== 'undefined' && typeof window.addEventListener === 'function';
    if (canListen) window.addEventListener('pagehide', flush);
    return () => {
      ignore = true;
      unsubscribe();
      if (canListen) window.removeEventListener('pagehide', flush);
      flush();
    };
  }, [store]);
  return <LocalPreferenceContext.Provider value={store}>
      {children}
    </LocalPreferenceContext.Provider>;
};
function _temp(s) {
  return s;
}